// High-accuracy geolocation helper.
//
// Why this exists: `navigator.geolocation.getCurrentPosition()` with a single
// callback very often returns a cached / Wi-Fi / IP based fix whose accuracy is
// hundreds of metres off. That made QR scan checks report "you are 200m away"
// even when the user was standing right next to the restaurant.
//
// This helper keeps watching the GPS until it gets a fix that is actually
// accurate (or until we run out of time), then returns the most precise reading.

// A fix at or below this accuracy immediately ends the watch.
export const GPS_TARGET_ACCURACY_METERS = 50;
// Fixes worse than this are considered unusable for radius checks.
export const GPS_MAX_ACCEPTABLE_ACCURACY_METERS = 500;

// The radius check only needs a coarse position — no need to sample 8 times.
// Sample a few readings: the first can be a stale Wi-Fi estimate, while a
// following GPS reading gives the radius check the customer's real location.
const GPS_MAX_SAMPLES = 3;
// A quick GPS attempt is followed by a normal-location fallback when needed.
const GPS_MAX_WAIT_MS = 10000;
const GPS_PER_SAMPLE_TIMEOUT_MS = 5000;
// A restaurant visit commonly lasts well beyond a few minutes. Reuse the
// verified branch-scoped position during that visit, then obtain a fresh fix.
const GPS_POSITION_CACHE_TTL_MS = 60 * 60 * 1000;
const positionCache = new Map();
const POSITION_CACHE_PREFIX = 'dineflow_verified_location:';

const getCachedPosition = (cacheKey) => {
  if (!cacheKey) return null;
  const isFresh = (entry) => entry
    && entry.position
    && Number.isFinite(entry.position.latitude)
    && Number.isFinite(entry.position.longitude)
    && Number.isFinite(entry.cachedAt)
    && Date.now() - entry.cachedAt <= GPS_POSITION_CACHE_TTL_MS;

  const memoryEntry = positionCache.get(cacheKey);
  if (isFresh(memoryEntry)) return memoryEntry.position;
  positionCache.delete(cacheKey);

  // sessionStorage survives a page refresh but is scoped to this browser tab.
  // It is deliberately not localStorage, so a later unrelated visit starts fresh.
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(`${POSITION_CACHE_PREFIX}${cacheKey}`));
    if (isFresh(saved)) {
      positionCache.set(cacheKey, saved);
      return saved.position;
    }
    window.sessionStorage.removeItem(`${POSITION_CACHE_PREFIX}${cacheKey}`);
  } catch (e) { /* storage may be unavailable in private browser modes */ }
  return null;
};

const cachePosition = (cacheKey, position) => {
  if (!cacheKey) return;
  const entry = { position, cachedAt: Date.now() };
  positionCache.set(cacheKey, entry);
  try {
    window.sessionStorage.setItem(`${POSITION_CACHE_PREFIX}${cacheKey}`, JSON.stringify(entry));
  } catch (e) { /* memory cache remains available */ }
};

export const isGeolocationAvailable = () =>
  typeof navigator !== 'undefined' && !!navigator.geolocation;

export const formatAccuracy = (accuracy) => {
  if (!Number.isFinite(accuracy)) return 'unknown';
  return `${Math.round(accuracy)} m`;
};

const toRadians = (value) => (value * Math.PI) / 180;

// Great-circle distance in metres (Haversine) — mirrors the backend helper.
export const haversineDistanceMeters = (lat1, lng1, lat2, lng2) => {
  const earthRadiusMeters = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;
  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const toSample = (position) => ({
  latitude: position.coords.latitude,
  longitude: position.coords.longitude,
  accuracy: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : Number.POSITIVE_INFINITY,
  timestamp: position.timestamp || Date.now()
});

const mapError = (err) => {
  const code =
    err && err.code === 1 ? 'PERMISSION_DENIED'
      : err && err.code === 2 ? 'POSITION_UNAVAILABLE'
        : err && err.code === 3 ? 'TIMEOUT'
          : 'NO_FIX';
  return { code, message: (err && err.message) || 'Location unavailable', original: err || null };
};

// Precision-weighted average, so the best fixes dominate the result.
const averageSamples = (samples) => {
  if (samples.length === 1) return { ...samples[0] };
  let latSum = 0;
  let lngSum = 0;
  let weightSum = 0;
  let bestAccuracy = Number.POSITIVE_INFINITY;
  let latest = 0;
  samples.forEach((sample) => {
    const accuracy = Math.max(sample.accuracy, 1);
    const weight = 1 / (accuracy * accuracy);
    latSum += sample.latitude * weight;
    lngSum += sample.longitude * weight;
    weightSum += weight;
    bestAccuracy = Math.min(bestAccuracy, sample.accuracy);
    latest = Math.max(latest, sample.timestamp);
  });
  return {
    latitude: latSum / weightSum,
    longitude: lngSum / weightSum,
    accuracy: bestAccuracy,
    timestamp: latest
  };
};

/**
 * Waits for the best achievable GPS fix.
 *
 * @param {Object}   [options]
 * @param {number}   [options.targetAccuracy]  stop early once a fix is this precise
 * @param {number}   [options.maxWaitMs]       give up after this long
 * @param {number}   [options.maxSamples]      give up after this many readings
 * @param {boolean}  [options.allowLowAccuracyFallback] return a poor fix instead of failing
 * @param {Function} [options.onUpdate]        called with each raw sample (for "Locating…" UI)
 * @returns {Promise<{latitude:number, longitude:number, accuracy:number, quality:string, samples:number}>}
 */
export const getHighAccuracyPosition = ({
  targetAccuracy = GPS_TARGET_ACCURACY_METERS,
  maxWaitMs = GPS_MAX_WAIT_MS,
  maxSamples = GPS_MAX_SAMPLES,
  allowLowAccuracyFallback = true,
  onUpdate = null,
  cacheKey = null
} = {}) => new Promise((resolve, reject) => {
  // Reuse a verified location briefly for another action in the same QR flow.
  // Callers supply a company/branch-specific key, so positions never cross branches.
  if (cacheKey) {
    const cached = getCachedPosition(cacheKey);
    if (cached) {
      resolve({ ...cached, cached: true });
      return;
    }
  }

  if (!isGeolocationAvailable()) {
    reject({ code: 'UNSUPPORTED', message: 'Geolocation is not supported by this device.' });
    return;
  }

  const samples = [];
  let settled = false;
  let watchId = null;
  let timer = null;
  let quickFallbackTimer = null;
  let lastError = null;
  let usingLowPowerFallback = false;

  const stop = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    if (quickFallbackTimer) { clearTimeout(quickFallbackTimer); quickFallbackTimer = null; }
    if (watchId !== null) {
      try { navigator.geolocation.clearWatch(watchId); } catch (e) { /* ignore */ }
      watchId = null;
    }
  };

  const finish = () => {
    if (settled) return;
    settled = true;
    stop();

    const usable = samples.filter((s) => s.accuracy <= GPS_MAX_ACCEPTABLE_ACCURACY_METERS);
    const pool = usable.length > 0 ? usable : (allowLowAccuracyFallback ? samples : []);

    if (pool.length === 0) {
      reject(lastError ? mapError(lastError) : { code: 'NO_FIX', message: 'Could not read your location.' });
      return;
    }

    // Prefer recent + precise readings, ignore stale ones (> 2 minutes old)
    const freshCutoff = Date.now() - 120000;
    let candidates = pool.filter((s) => s.timestamp >= freshCutoff);
    if (candidates.length === 0) candidates = pool;

    const bestAccuracy = Math.min(...candidates.map((s) => s.accuracy));
    const tolerance = Math.max(bestAccuracy * 2, 30);
    const chosen = candidates.filter((s) => s.accuracy <= tolerance);
    const merged = averageSamples(chosen.length > 0 ? chosen : candidates);

    const result = {
      latitude: merged.latitude,
      longitude: merged.longitude,
      accuracy: merged.accuracy,
      quality: merged.accuracy <= targetAccuracy ? 'high' : (merged.accuracy <= GPS_MAX_ACCEPTABLE_ACCURACY_METERS ? 'medium' : 'low'),
      samples: samples.length
    };
    cachePosition(cacheKey, result);
    resolve(result);
  };

  const handleSuccess = (position) => {
    const sample = toSample(position);
    samples.push(sample);
    if (typeof onUpdate === 'function') {
      try { onUpdate(sample); } catch (e) { /* ignore UI errors */ }
    }
    if (sample.accuracy <= targetAccuracy || samples.length >= maxSamples) finish();
  };

  const handleError = (err) => {
    lastError = err;
    // A denied permission will never recover while watching — stop immediately.
    if (err && err.code === 1) {
      finish();
      return;
    }
    // GPS can time out indoors even when a normal Wi-Fi/cell location is
    // available. Retry once before declaring the signal unusable.
    if (err && (err.code === 2 || err.code === 3) && samples.length === 0 && !usingLowPowerFallback) {
      usingLowPowerFallback = true;
      if (watchId !== null) {
        try { navigator.geolocation.clearWatch(watchId); } catch (e) { /* ignore */ }
      }
      watchId = navigator.geolocation.watchPosition(handleSuccess, handleError, {
        enableHighAccuracy: false,
        timeout: GPS_PER_SAMPLE_TIMEOUT_MS,
        maximumAge: 120000
      });
    }
  };

  try {
    watchId = navigator.geolocation.watchPosition(handleSuccess, handleError, {
      enableHighAccuracy: true,
      timeout: GPS_PER_SAMPLE_TIMEOUT_MS,
      maximumAge: 60000   // accept a cached fix up to 60 s old — returns instantly when available
    });
  } catch (err) {
    reject(mapError(err));
    return;
  }

  timer = setTimeout(finish, maxWaitMs);

  // Do not wait for the high-accuracy provider to time out before asking the
  // device for a normal cached/Wi-Fi/cell fix. This is especially important
  // when a QR opens the browser for the first time in an indoor restaurant.
  quickFallbackTimer = setTimeout(() => {
    if (settled || samples.length > 0) return;
    try {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
        enableHighAccuracy: false,
        timeout: 3000,
        maximumAge: 120000
      });
    } catch (err) { /* the high-accuracy watcher remains active */ }
  }, 1000);
});

/**
 * Single reading — used when the caller only needs a rough position quickly.
 * Returns the first available fix (cached or fresh). Max wait: 4 seconds.
 */
export const getQuickPosition = ({ timeout = 4000 } = {}) => new Promise((resolve, reject) => {
  if (!isGeolocationAvailable()) {
    reject({ code: 'UNSUPPORTED', message: 'Geolocation is not supported by this device.' });
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (position) => resolve(toSample(position)),
    (err) => reject(mapError(err)),
    { enableHighAccuracy: true, timeout, maximumAge: 60000 }
  );
});

